import React, { useMemo, useState } from "react";
import { Card, Descriptions, Divider, Drawer, Input, Select, Table, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLoyaltyUsers, useLoyaltyClaims, useLoyaltyRewards } from "data/hooks";
import dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";

const SHOP_SOURCE_COLOR = { VCE: "green", HVO: "purple" };

const LoyaltyUsers = () => {
  const { t } = useTranslation();
  const { loyaltyUsers, isLoading: usersLoading } = useLoyaltyUsers();
  const { getClaimsByUserId } = useLoyaltyClaims();
  const { loyaltyRewards } = useLoyaltyRewards();

  const [searchText, setSearchText] = useState("");
  const [shopFilter, setShopFilter] = useState("all");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const filteredList = useMemo(() => {
    if (!loyaltyUsers) return [];
    let list = loyaltyUsers;

    if (shopFilter !== "all") {
      list = list.filter((user) => user.shopSource === shopFilter);
    }

    if (searchText) {
      const q = searchText.toLowerCase();
      list = list.filter(
        (user) =>
          user.id.toLowerCase().includes(q) ||
          (user.email && user.email.toLowerCase().includes(q)) ||
          (user.firstName && user.firstName.toLowerCase().includes(q)) ||
          (user.lastName && user.lastName.toLowerCase().includes(q)),
      );
    }

    return list;
  }, [loyaltyUsers, searchText, shopFilter]);

  const getRewardById = (rewardId, shopSource) => {
    if (!loyaltyRewards || !rewardId) return null;
    return (
      loyaltyRewards.find(
        (reward) => reward.id === rewardId && reward.shopSource === shopSource,
      ) || loyaltyRewards.find((reward) => reward.id === rewardId) || null
    );
  };

  const tableColumns = [
    {
      title: "Shop",
      dataIndex: "shopSource",
      width: 80,
      render: (data) => <Tag color={SHOP_SOURCE_COLOR[data]}>{data}</Tag>,
    },
    {
      title: t("email"),
      dataIndex: "email",
      render: (data, record) => (
        <a
          onClick={() => {
            setSelectedUser(record);
            setDrawerVisible(true);
          }}
          style={{ cursor: "pointer", fontWeight: 500 }}
        >
          {data || record.id}
        </a>
      ),
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: t("name"),
      dataIndex: "firstName",
      render: (_, record) =>
        record.firstName && record.lastName
          ? `${record.firstName} ${record.lastName}`
          : "-",
    },
    {
      title: t("loyalty.current_points"),
      dataIndex: "currentPoints",
      render: (data) => (
        <Tag color="blue" style={{ fontSize: 14, padding: "2px 8px" }}>
          {data || 0}
        </Tag>
      ),
      sorter: (a, b) => (a.currentPoints || 0) - (b.currentPoints || 0),
    },
    {
      title: t("loyalty.total_points_earned"),
      dataIndex: "totalPointsEarned",
      render: (data) => <span>{data || 0}</span>,
      sorter: (a, b) => (a.totalPointsEarned || 0) - (b.totalPointsEarned || 0),
    },
    {
      title: t("loyalty.total_points_spent"),
      dataIndex: "totalPointsSpent",
      render: (data) => <span>{data || 0}</span>,
      sorter: (a, b) => (a.totalPointsSpent || 0) - (b.totalPointsSpent || 0),
    },
    {
      title: t("loyalty.total_points_cashed_in"),
      dataIndex: "totalPointsCashedIn",
      render: (data) => <span>{data || 0}</span>,
      sorter: (a, b) => (a.totalPointsCashedIn || 0) - (b.totalPointsCashedIn || 0),
    },
    {
      title: t("loyalty.created_at"),
      dataIndex: "createdAt",
      render: (data) => (data ? dayjs(data * 1000).format(DATE_FORMAT_DD_MM_YYYY) : "-"),
      sorter: (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
    },
  ];

  const userClaims = selectedUser
    ? getClaimsByUserId(selectedUser.id, selectedUser.shopSource)
    : [];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input
            value={searchText}
            placeholder={t("loyalty.search_users")}
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.currentTarget.value)}
            style={{ width: 300 }}
          />
          <Select
            defaultValue="all"
            style={{ minWidth: 140 }}
            onChange={(value) => setShopFilter(value)}
            options={[
              { label: t("all"), value: "all" },
              { label: "VCE", value: "VCE" },
              { label: "HVO", value: "HVO" },
            ]}
          />
        </div>
      </div>
      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={tableColumns}
          dataSource={filteredList}
          rowKey={(record) => `${record.shopSource}_${record.id}`}
          loading={usersLoading}
          scroll={{ x: 1100 }}
        />
      </div>
      <Drawer
        title={
          selectedUser
            ? `${selectedUser.firstName || ""} ${selectedUser.lastName || ""} (${selectedUser.shopSource})`.trim()
            : t("loyalty.user_details")
        }
        placement="right"
        width={720}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedUser(null);
        }}
        open={drawerVisible}
      >
        {selectedUser && (
          <div>
            <Descriptions title={t("loyalty.points_overview")} bordered column={1}>
              <Descriptions.Item label="Shop">
                <Tag color={SHOP_SOURCE_COLOR[selectedUser.shopSource]}>{selectedUser.shopSource}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t("email")}>{selectedUser.email || "-"}</Descriptions.Item>
              <Descriptions.Item label={t("name")}>
                {selectedUser.firstName && selectedUser.lastName
                  ? `${selectedUser.firstName} ${selectedUser.lastName}`
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("loyalty.user_id")}>{selectedUser.id}</Descriptions.Item>
              <Descriptions.Item label={t("loyalty.current_points")}>
                <Tag color="blue" style={{ fontSize: 16, padding: "4px 12px" }}>
                  {selectedUser.currentPoints || 0} {t("loyalty.points")}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t("loyalty.total_points_earned")}>
                {selectedUser.totalPointsEarned || 0} {t("loyalty.points")}
              </Descriptions.Item>
              <Descriptions.Item label={t("loyalty.total_points_spent")}>
                {selectedUser.totalPointsSpent || 0} {t("loyalty.points")}
              </Descriptions.Item>
              <Descriptions.Item label={t("loyalty.total_points_cashed_in")}>
                {selectedUser.totalPointsCashedIn || 0} {t("loyalty.points")}
              </Descriptions.Item>
              <Descriptions.Item label={t("loyalty.points_to_expire")}>
                {selectedUser.pointsToExpire || 0} {t("loyalty.points")}
              </Descriptions.Item>
              <Descriptions.Item label={t("loyalty.created_at")}>
                {selectedUser.createdAt
                  ? dayjs(selectedUser.createdAt * 1000).format(DATE_FORMAT_DD_MM_YYYY)
                  : "-"}
              </Descriptions.Item>
              {selectedUser.frozenAt && (
                <Descriptions.Item label={t("loyalty.frozen_at")}>
                  {dayjs(selectedUser.frozenAt * 1000).format(DATE_FORMAT_DD_MM_YYYY)}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">{t("loyalty.claims")}</Divider>
            {userClaims.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {userClaims.map((claim) => {
                  const reward = getRewardById(claim.rewardId, selectedUser.shopSource);
                  return (
                    <Card
                      key={claim.id}
                      size="small"
                      style={{
                        borderLeft: claim.cashedIn ? "4px solid #52c41a" : "4px solid #faad14",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 8 }}>
                            <strong>{reward?.title || t("loyalty.unknown_reward")}</strong>
                          </div>
                          {reward?.description && (
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              {reward.description}
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <Tag color="blue">
                              {claim.points} {t("loyalty.points")}
                            </Tag>
                            <Tag color={claim.cashedIn ? "green" : "orange"}>
                              {claim.cashedIn ? t("loyalty.cashed_in") : t("loyalty.not_cashed_in")}
                            </Tag>
                            {claim.rewardType && (
                              <Tag>
                                {claim.rewardType === "free_product"
                                  ? t("loyalty.free_product")
                                  : claim.rewardType}
                              </Tag>
                            )}
                            {claim.quantity > 1 && (
                              <Tag color="purple">
                                {claim.quantity}x
                              </Tag>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", minWidth: 100 }}>
                          <div style={{ fontSize: 12, color: "#999" }}>
                            {dayjs(claim.dateTime * 1000).format(DATE_FORMAT_DD_MM_YYYY)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p>{t("loyalty.no_claims")}</p>
            )}
          </div>
        )}
      </Drawer>
    </Card>
  );
};

export default LoyaltyUsers;
